import React from 'react';
import { Shield, Award, Star, Crown, Clock, Users, FileCheck, CheckCircle, AlertTriangle } from 'lucide-react';

const categories = [
  {
    id: 'active', icon: Shield, gradient: 'from-cmic-600 to-cmic-800', accent: 'cmic',
    badge: { es: 'Miembros Activos', en: 'Active Members' },
    requirements: [
      { icon: Clock, label: { es: 'Requisitos', en: 'Requirements' }, text: { es: 'Comprobar 200 días de trabajo como intérprete de conferencias en un plazo de 3 años consecutivos.', en: 'Demonstrate 200 days of work as a conference interpreter within 3 consecutive years.' } },
      { icon: Users, label: { es: 'Avales', en: 'Endorsements' }, text: { es: 'Tres Miembros Activos que hayan trabajado con el solicitante.', en: 'Three Active Members who have worked with the applicant.' } },
      { icon: FileCheck, label: { es: 'Admisión a priori', en: 'Prior Admission' }, text: { es: 'Pre-admisión inmediata por 1 año tras entregar avales y documentación.', en: 'Immediate pre-admission for 1 year after submitting endorsements and documentation.' } },
    ],
    obligationsText: { es: 'Los Miembros Activos están sujetos al pago de las cuotas que establezca la Asamblea General. Tienen derecho a voz y voto en las juntas y asambleas del CMIC.', en: 'Active Members pay fees set by the General Assembly and have voice and vote rights.' },
  },
  {
    id: 'candidate', icon: Star, gradient: 'from-teal-600 to-teal-800', accent: 'teal',
    badge: { es: 'Candidatos a Miembro', en: 'Candidate Members' },
    requirements: [
      { icon: Clock, label: { es: 'Requisitos', en: 'Requirements' }, text: { es: 'Comprobar 50 días de trabajo como intérprete de conferencias en un plazo de 2 años.', en: 'Demonstrate 50 days of work within 2 years.' } },
      { icon: Users, label: { es: 'Avales', en: 'Endorsements' }, text: { es: 'Tres miembros Activos que hayan trabajado con el solicitante.', en: 'Three Active Members who have worked with the applicant.' } },
    ],
    obligationsText: { es: 'Los Candidatos a Miembro pagan el 50% de las cuotas. Tienen voz sin voto. Permanencia máxima 2 años (prorrogables una vez).', en: 'Candidate Members pay 50% of fees. Voice without vote. Maximum 2 years (extendable once).' },
  },
  {
    id: 'senior', icon: Crown, gradient: 'from-amber-600 to-amber-800', accent: 'amber',
    badge: { es: 'Miembros Senior', en: 'Senior Members' },
    requirements: [
      { icon: CheckCircle, label: { es: 'Requisitos', en: 'Requirements' }, text: { es: 'Más de 75 años de edad y 20 años ininterrumpidos como miembros Activos.', en: 'Over 75 years old with 20 uninterrupted years as Active members.' } },
    ],
    obligationsText: { es: 'Los Miembros Senior pagan el 50% de las cuotas y tienen derecho a voz y voto.', en: 'Senior Members pay 50% of fees and have voice and vote rights.' },
  },
  {
    id: 'honorary', icon: Award, gradient: 'from-rose-600 to-rose-800', accent: 'rose',
    badge: { es: 'Miembros Honorarios', en: 'Honorary Members' },
    requirements: [],
    obligationsText: { es: 'Pagan el 50% de las cuotas. Voz pero no voto.', en: 'Pay 50% of fees. Voice but no vote.' },
  },
];

const accentStyles = {
  cmic: { iconBg: 'bg-cmic-100', iconText: 'text-cmic-700', labelBg: 'bg-cmic-50', labelText: 'text-cmic-700', obligationBg: 'bg-cmic-50/40', obligationBorder: 'border-l-cmic-400' },
  teal: { iconBg: 'bg-teal-100', iconText: 'text-teal-700', labelBg: 'bg-teal-50', labelText: 'text-teal-700', obligationBg: 'bg-teal-50/40', obligationBorder: 'border-l-teal-400' },
  amber: { iconBg: 'bg-amber-100', iconText: 'text-amber-700', labelBg: 'bg-amber-50', labelText: 'text-amber-700', obligationBg: 'bg-amber-50/40', obligationBorder: 'border-l-amber-400' },
  rose: { iconBg: 'bg-rose-100', iconText: 'text-rose-700', labelBg: 'bg-rose-50', labelText: 'text-rose-700', obligationBg: 'bg-rose-50/40', obligationBorder: 'border-l-rose-400' },
};

export default function MembershipCards({ locale }) {
  return (
    <div className="space-y-6">
      <div className="px-1">
        <span className="inline-block text-xs font-bold text-cmic-600 bg-cmic-50 rounded px-2 py-0.5 mb-3">
          {locale === 'es' ? 'Artículo' : 'Article'} 5
        </span>
        <p className="text-charcoal-600 text-sm leading-relaxed">
          {locale === 'es' ? 'Cualquier persona física que cumpla con los requisitos podrá ser miembro del CMIC en alguna de las siguientes categorías:' : 'Any individual who meets the requirements may be a member of CMIC in one of the following categories:'}
        </p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {categories.map((cat) => {
          const Icon = cat.icon;
          const styles = accentStyles[cat.accent];
          return (
            <div key={cat.id} className="group relative rounded-2xl border border-charcoal-100 bg-white overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5">
              <div className={`bg-gradient-to-r ${cat.gradient} px-5 py-4 flex items-center gap-3`}>
                <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                  <Icon size={20} className="text-white" />
                </div>
                <h4 className="text-white font-bold text-base tracking-wide">{cat.badge[locale]}</h4>
              </div>
              <div className="p-5 space-y-4">
                {cat.requirements.length > 0 && (
                  <div className="space-y-3">
                    {cat.requirements.map((req, ri) => {
                      const ReqIcon = req.icon;
                      return (
                        <div key={ri} className="flex gap-3">
                          <div className={`w-8 h-8 rounded-lg ${styles.iconBg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                            <ReqIcon size={15} className={styles.iconText} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className={`inline-block text-xs font-semibold ${styles.labelText} ${styles.labelBg} rounded px-1.5 py-0.5 mb-1`}>{req.label[locale]}</span>
                            <p className="text-charcoal-600 text-[13px] leading-relaxed">{req.text[locale]}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                <div className={`border-l-[3px] ${styles.obligationBorder} ${styles.obligationBg} rounded-r-lg p-4`}>
                  <span className={`inline-block text-[11px] font-bold uppercase tracking-wider ${styles.labelText} mb-2`}>
                    {locale === 'es' ? 'Cuotas y Derechos' : 'Fees & Rights'}
                  </span>
                  <p className="text-charcoal-700 text-[13px] leading-relaxed whitespace-pre-line">{cat.obligationsText[locale]}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
